import { useState, useMemo } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useOrgStore } from '@/stores/orgStore'
import { usePublishPageData } from '@/stores/pageDataStore'
import { policiesApi } from '@/api/clients/policiesApi'
import { assetsApi } from '@/api/clients/assetsApi'
import type { Rule, PolicySimResult } from '@/api/types'
import { PageHeader, Card, Button, Field, TextInput, TextArea, SelectInput } from '@/components/common/ui'
import { Badge } from '@/components/common/Badge'
import { Modal } from '@/components/common/Modal'
import { Spinner, ErrorState, EmptyState } from '@/components/common/State'
import { RoleGate } from '@/components/common/RoleGate'
import { RuleEditor } from '@/components/policy/RuleEditor'
import { RulePreview } from '@/components/policy/RulePreview'
import { formatDate } from '@/components/common/format'

export default function PolicyDetail() {
  const { id = '' } = useParams()
  const { currentOrgId: orgId } = useOrgStore()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [editRule, setEditRule] = useState<Rule | null>(null)
  const [showRule, setShowRule] = useState(false)
  const [showClone, setShowClone] = useState(false)
  const [deleteRule, setDeleteRule] = useState<Rule | null>(null)

  const policy = useQuery({
    queryKey: ['policy', orgId, id],
    queryFn: () => policiesApi.get(orgId, id),
    enabled: !!orgId && !!id,
  })
  const activations = useQuery({
    queryKey: ['activations', orgId, id],
    queryFn: () => policiesApi.activations(orgId, id),
    enabled: !!orgId && !!id,
  })
  const history = useQuery({
    queryKey: ['policy-history', orgId, id],
    queryFn: () => policiesApi.history(orgId, id),
    enabled: !!orgId && !!id,
  })

  usePublishPageData(
    useMemo(
      () =>
        policy.data
          ? {
              page: 'Policy detail',
              name: policy.data.name,
              engine_type: policy.data.engine_type,
              strategy: policy.data.strategy,
              version: policy.data.version,
              rules: policy.data.rules,
              activations: activations.data,
            }
          : null,
      [policy.data, activations.data]
    )
  )

  const deactivate = useMutation({
    mutationFn: (a: import('@/api/types').Activation) =>
      policiesApi.deactivate(orgId, id, {
        scope_type: a.scope_type,
        scope_id: a.scope_id ?? undefined,
        rationale: 'deactivated via portal',
      }),
    onSuccess: () => activations.refetch(),
  })
  const exportYaml = useMutation({
    mutationFn: () => policiesApi.exportYaml(orgId, id),
    onSuccess: (yaml) => {
      const blob = new Blob([yaml], { type: 'application/x-yaml' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${policy.data?.name ?? 'policy'}.yaml`
      link.click()
      URL.revokeObjectURL(url)
    },
  })

  if (policy.isLoading) return <Spinner />
  if (policy.error) return <ErrorState error={policy.error} />
  const p = policy.data!

  return (
    <div>
      <div className="mb-2">
        <Link
          to="/policies"
          className="inline-flex items-center gap-1 text-sm text-subtle-light dark:text-subtle-dark hover:text-primary dark:hover:text-secondary"
        >
          <span className="material-icons text-base">arrow_back</span> Policies
        </Link>
      </div>
      <PageHeader
        title={p.name}
        subtitle={`${p.engine_type} engine · ${p.strategy} · v${p.version}`}
        actions={
          <>
            {p.is_shipped_default && (
              <Badge label="shipped default — clone to edit" variant="neutral" />
            )}
            <Button icon="download" onClick={() => exportYaml.mutate()}>
              Export YAML
            </Button>
            <RoleGate required="policy_author">
              <Button icon="content_copy" onClick={() => setShowClone(true)}>
                Clone
              </Button>
            </RoleGate>
            {/* Shipped defaults are read-only (PUT rejected) — must clone first (§5.3.10). */}
            {!p.is_shipped_default && (
              <RoleGate required={['policy_author', 'security_owner']}>
                <Button
                  variant="primary"
                  icon="add"
                  onClick={() => {
                    setEditRule(null)
                    setShowRule(true)
                  }}
                >
                  Add rule
                </Button>
              </RoleGate>
            )}
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Rules */}
        <Card title="Rules" className="!p-0 lg:col-span-2">
          {p.rules?.length ? (
            <ul className="divide-y divide-border-light dark:divide-border-dark">
              {p.rules.map((r) => (
                <li key={r.rule_id} className="px-5 py-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-xs text-primary dark:text-secondary">
                      {r.rule_id}
                    </span>
                    <span className="text-xs text-subtle-light dark:text-subtle-dark">
                      priority {r.priority}
                    </span>
                    {r.framework_ref?.map((f) => (
                      <Badge key={f} label={f} variant="neutral" />
                    ))}
                    {!p.is_shipped_default && (
                      <div className="ml-auto flex gap-1">
                        <RoleGate required="policy_author">
                          <Button
                            variant="ghost"
                            icon="edit"
                            onClick={() => {
                              setEditRule(r)
                              setShowRule(true)
                            }}
                          />
                          <Button variant="ghost" icon="delete" onClick={() => setDeleteRule(r)} />
                        </RoleGate>
                      </div>
                    )}
                  </div>
                  <RulePreview rule={r} />
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon="rule" title="No rules yet" />
          )}
        </Card>

        <div className="space-y-4">
          {/* Activations */}
          <Card title="Activations">
            {activations.data?.length ? (
              <ul className="space-y-2 text-sm">
                {activations.data.map((a) => (
                  <li key={a.id} className="flex items-center gap-2">
                    <Badge label={a.scope_type} variant="info" />
                    <span className="flex-1 text-subtle-light dark:text-subtle-dark truncate">
                      {a.scope_id ?? 'org-wide'}
                    </span>
                    <RoleGate required="security_owner">
                      <Button
                        variant="ghost"
                        icon="stop"
                        onClick={() => deactivate.mutate(a)}
                        title="Deactivate"
                      />
                    </RoleGate>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-subtle-light dark:text-subtle-dark">Not activated.</p>
            )}
            <RoleGate required="security_owner">
              <ActivateForm orgId={orgId} pid={id} onDone={() => activations.refetch()} />
            </RoleGate>
          </Card>

          {/* History */}
          <Card title="Audit trail">
            {history.data?.length ? (
              <ul className="space-y-2 text-xs">
                {history.data.slice(0, 10).map((h, i) => (
                  <li key={i} className="text-subtle-light dark:text-subtle-dark">
                    <span className="text-text-light dark:text-text-dark">{h.action}</span> ·{' '}
                    {h.actor} · {formatDate(h.at)}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-subtle-light dark:text-subtle-dark">No history.</p>
            )}
          </Card>
        </div>
      </div>

      {showRule && (
        <RuleEditor
          orgId={orgId}
          pid={id}
          rule={editRule}
          onClose={() => setShowRule(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['policy', orgId, id] })
            setShowRule(false)
          }}
        />
      )}

      {showClone && (
        <ClonePolicyModal
          orgId={orgId}
          pid={id}
          defaultName={`${p.name} (copy)`}
          onClose={() => setShowClone(false)}
          onCloned={(newId) => {
            setShowClone(false)
            navigate(`/policies/${newId}`)
          }}
        />
      )}

      {deleteRule && (
        <DeleteRuleModal
          orgId={orgId}
          pid={id}
          rule={deleteRule}
          onClose={() => setDeleteRule(null)}
          onDone={() => {
            qc.invalidateQueries({ queryKey: ['policy', orgId, id] })
            setDeleteRule(null)
          }}
        />
      )}
    </div>
  )
}

function DeleteRuleModal({
  orgId,
  pid,
  rule,
  onClose,
  onDone,
}: {
  orgId: string
  pid: string
  rule: Rule
  onClose: () => void
  onDone: () => void
}) {
  const [rationale, setRationale] = useState('')
  const mut = useMutation({
    mutationFn: () => policiesApi.removeRule(orgId, pid, rule.rule_id, rationale),
    onSuccess: onDone,
  })
  return (
    <Modal
      title={`Remove rule ${rule.rule_id}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="danger"
            disabled={rationale.length < 20 || mut.isPending}
            onClick={() => mut.mutate()}
          >
            {mut.isPending ? 'Removing…' : 'Remove'}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <p className="text-subtle-light dark:text-subtle-dark">
          Tombstones the rule (kept for audit). This affects future scoring.
        </p>
        <Field label="Rationale (≥ 20 chars, audit)">
          <TextArea rows={3} value={rationale} onChange={(e) => setRationale(e.target.value)} />
        </Field>
        {mut.error && <p className="text-error">{(mut.error as Error).message}</p>}
      </div>
    </Modal>
  )
}

function ClonePolicyModal({
  orgId,
  pid,
  defaultName,
  onClose,
  onCloned,
}: {
  orgId: string
  pid: string
  defaultName: string
  onClose: () => void
  onCloned: (id: string) => void
}) {
  const [name, setName] = useState(defaultName)
  const [rationale, setRationale] = useState('')
  const mut = useMutation({
    mutationFn: () => policiesApi.clone(orgId, pid, name, rationale),
    onSuccess: (p) => onCloned(p.id),
  })
  return (
    <Modal
      title="Clone policy"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!name || rationale.length < 20 || mut.isPending} onClick={() => mut.mutate()}>
            Clone
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="New name">
          <TextInput value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Rationale (audit)">
          <TextInput value={rationale} onChange={(e) => setRationale(e.target.value)} />
        </Field>
        {mut.error && <p className="text-sm text-error">{(mut.error as Error).message}</p>}
      </div>
    </Modal>
  )
}

function ActivateForm({ orgId, pid, onDone }: { orgId: string; pid: string; onDone: () => void }) {
  const { currentOrgId } = useOrgStore()
  const DRY_RUN_LIMIT = 1000
  const [scope, setScope] = useState('org')
  const [scopeId, setScopeId] = useState('')
  const [rationale, setRationale] = useState('')
  const [diff, setDiff] = useState<PolicySimResult[] | null>(null)
  const [sampled, setSampled] = useState(false)

  // Dry-run required before activation (§5.3.5): simulate against current assets,
  // show band-change diff. Activate stays disabled until a dry-run has run.
  const dryRun = useMutation({
    mutationFn: async () => {
      const assets = await assetsApi.list(currentOrgId, { limit: DRY_RUN_LIMIT })
      setSampled(assets.length >= DRY_RUN_LIMIT)
      return policiesApi.simulate(currentOrgId, assets)
    },
    onSuccess: (res) => setDiff(res),
  })
  const activate = useMutation({
    mutationFn: () =>
      policiesApi.activate(orgId, pid, {
        scope_type: scope,
        scope_id: scope === 'org' ? undefined : scopeId || undefined,
        rationale,
      }),
    onSuccess: onDone,
  })

  const changed = diff?.filter((d) => d.changed).length ?? 0
  const canActivate =
    rationale.length >= 20 && !!diff && !activate.isPending && (scope === 'org' || !!scopeId)

  return (
    <div className="mt-3 pt-3 border-t border-border-light dark:border-border-dark space-y-2">
      {/* Scoring honours org- and inventory-scope activations only (§5.3). */}
      <SelectInput value={scope} onChange={(e) => setScope(e.target.value)}>
        {['org', 'inventory'].map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </SelectInput>
      {scope !== 'org' && (
        <TextInput
          value={scopeId}
          onChange={(e) => setScopeId(e.target.value)}
          placeholder={`${scope} id (UUID)`}
        />
      )}
      <TextInput value={rationale} onChange={(e) => setRationale(e.target.value)} placeholder="Activation rationale" />

      <Button
        icon="science"
        className="w-full"
        disabled={dryRun.isPending}
        onClick={() => dryRun.mutate()}
      >
        {dryRun.isPending ? 'Simulating…' : 'Dry-run (required)'}
      </Button>
      {dryRun.error && <p className="text-xs text-error">{(dryRun.error as Error).message}</p>}
      {diff && (
        <p className="text-xs text-subtle-light dark:text-subtle-dark">
          Dry-run: {changed} of {diff.length} assets change band
          {sampled ? ` (first ${DRY_RUN_LIMIT} sampled)` : ''}.
        </p>
      )}

      <Button
        variant="primary"
        icon="play_arrow"
        className="w-full"
        disabled={!canActivate}
        title={!diff ? 'Run a dry-run first' : undefined}
        onClick={() => activate.mutate()}
      >
        Activate ({scope})
      </Button>
      {activate.error && <p className="text-xs text-error">{(activate.error as Error).message}</p>}
    </div>
  )
}

